import React, { useState, useEffect, useCallback, useMemo, useContext, useRef } from 'react';
import { FaSpinner, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { useNavigate, useLocation } from 'react-router-dom';
// Import styles for results history
import styles from './ListingScoreResultHistory.module.css';
import ResultsSection from '../components/ResultsSection';
import ActionPlanSection from '../components/ActionPlanSection';
import ComponentAnalysisSection from '../components/ComponentAnalysisSection';
import listingScoreStyles from '../ListingScore.module.css';
import AuthContext from '../../../context/AuthContext';
import ListingScoreDownloadButton from '../ListingScoreDownloadButton';
import { navigateToSingleListingUpgrade } from '../../../utils/upgradeListingNavigation';
import { transformScoreData } from '../scoreUtils';
import SingleScoreCard from '../../../pages/Manage/components/SingleScoreCard';
import BulkScoreCard from '../../../pages/Manage/components/BulkScoreCard';
import manageStyles from '../../../pages/Manage/ManagePage.module.css';

// Constants
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const PRODUCTS_PER_PAGE = 100;

// Pagination Component (reused from Catalogue)
const Pagination = React.memo(({ currentPage, totalPages, onPageChange }) => {
    const handlePrevPage = useCallback(() => onPageChange(currentPage - 1), [currentPage, onPageChange]);
    const handleNextPage = useCallback(() => onPageChange(currentPage + 1), [currentPage, onPageChange]);
    
    if (totalPages <= 1) return null;

    return (
        <div className={styles.pagination}>
            <button
                onClick={handlePrevPage}
                disabled={currentPage === 1}
                className={styles.paginationButton}
                aria-label="Previous page"
            >
                <FaChevronLeft />
            </button>
            
            <span className={styles.pageInfo}>
                Page {currentPage} of {totalPages}
            </span>
            
            <button
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                className={styles.paginationButton}
                aria-label="Next page"
            >
                <FaChevronRight />
            </button>
        </div>
    );
});

// Groups flat results by task_id. Returns array of { task_id, completed_at, results } sorted by completed_at desc.
function groupResultsByBatch(flatResults) {
    const byTask = {};
    for (const r of flatResults) {
        const tid = r.task_id || 'unknown';
        if (!byTask[tid]) byTask[tid] = { task_id: tid, completed_at: r.completed_at, results: [] };
        byTask[tid].results.push(r);
        if (r.completed_at && (!byTask[tid].completed_at || new Date(r.completed_at) > new Date(byTask[tid].completed_at)))
            byTask[tid].completed_at = r.completed_at;
    }
    return Object.values(byTask).sort((a, b) => {
        const da = a.completed_at ? new Date(a.completed_at) : new Date(0);
        const db = b.completed_at ? new Date(b.completed_at) : new Date(0);
        return db - da;
    });
}

// Results Grid Component (card-based layout)
const ResultsGrid = React.memo(({ results, onViewScore, currentPage, productsPerPage }) => {
    const currentResults = useMemo(() => {
        const startIndex = (currentPage - 1) * productsPerPage;
        const endIndex = startIndex + productsPerPage;
        return results.slice(startIndex, endIndex);
    }, [results, currentPage, productsPerPage]);

    const startIndex = useMemo(() => (currentPage - 1) * productsPerPage, [currentPage, productsPerPage]);

    return (
        <div className={manageStyles.smallCardsGrid}>
            {currentResults.map((result, index) => (
                <SingleScoreCard
                    key={`${result.asin}-${startIndex + index}`}
                    result={result}
                    onViewScore={onViewScore}
                />
            ))}
        </div>
    );
});

// Main Component
const ListingScoreResultHistory = () => {
    const authCtx = useContext(AuthContext);
    const navigate = useNavigate();
    const location = useLocation();
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedBatchId, setSelectedBatchId] = useState(null);
    const [selectedScoreData, setSelectedScoreData] = useState(null);
    const [showDetailedView, setShowDetailedView] = useState(false);

    // Fetch listing score history
    const fetchListingScoreHistory = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            setCurrentPage(1);
            setSelectedBatchId(null);
            const token = authCtx.token;
            if (!token) {
                throw new Error('No authentication token found. Please log in again.');
            }
            
            const response = await fetch(`${API_BASE_URL}/listing_score/all-scores`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch results: ${response.status}`);
            }

            const data = await response.json();
            
            if (data.success && data.results) {
                // Sort results by completed_at (most recent first)
                const sortedResults = [...data.results].sort((a, b) => {
                    const dateA = a.completed_at ? new Date(a.completed_at) : new Date(0);
                    const dateB = b.completed_at ? new Date(b.completed_at) : new Date(0);
                    return dateB - dateA; // Descending order (most recent first)
                });
                setResults(sortedResults);
            } else {
                setResults([]);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [authCtx.token]);

    useEffect(() => {
        if (authCtx.isLoggedIn) {
            fetchListingScoreHistory();
        } else {
            setLoading(false);
            setError('Please log in to view results');
        }
    }, [authCtx.isLoggedIn, fetchListingScoreHistory]);

    const handleUpgradeListing = useCallback((productData) => {
        navigateToSingleListingUpgrade(navigate, productData);
    }, [navigate]);

    const batches = useMemo(() => groupResultsByBatch(results), [results]);
    const selectedBatch = useMemo(() => batches.find(b => b.task_id === selectedBatchId), [batches, selectedBatchId]);
    const appliedBatchFromStateRef = useRef(false);

    // Open batch from Manage page (location.state.selectedBatchId) once on load
    useEffect(() => {
        const fromState = location.state?.selectedBatchId;
        if (fromState && !appliedBatchFromStateRef.current && batches.some((b) => b.task_id === fromState)) {
            appliedBatchFromStateRef.current = true;
            setSelectedBatchId(fromState);
            setCurrentPage(1);
        }
    }, [batches, location.state?.selectedBatchId]);
    const batchResults = selectedBatch ? selectedBatch.results : [];
    const hasBatches = batches.length > 0;
    const batchTotalPages = Math.ceil(batchResults.length / PRODUCTS_PER_PAGE);
    const handleBackToBatches = useCallback(() => {
        setSelectedBatchId(null);
        setCurrentPage(1);
    }, []);
    const handleBatchPageChange = useCallback((newPage) => {
        setCurrentPage(Math.max(1, Math.min(newPage, batchTotalPages)));
    }, [batchTotalPages]);

    const handleViewScore = useCallback((asin, scoreData) => {
        if (!scoreData || !scoreData.has_score) return;
        setSelectedScoreData(transformScoreData(asin, scoreData));
        setShowDetailedView(true);
    }, []);

    const handleCloseDetailedView = useCallback(() => {
        setShowDetailedView(false);
        setSelectedScoreData(null);
    }, []);

    // Loading state
    if (loading) {
        return (
            <div className={styles.container}>
                <div className={styles.detailedViewHeader}>
                    <button 
                        className={styles.backButton}
                        onClick={() => navigate(-1)}
                        aria-label="Back"
                    >
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M20 8.75H4.7875L11.775 1.7625L10 0L0 10L10 20L11.7625 18.2375L4.7875 11.25H20V8.75Z" fill="#737373"/>
                        </svg>
                    </button>
                    <div className={styles.detailedViewHeaderContent}>
                        <h1 className={styles.detailedViewTitle}>Result History</h1>
                        <p className={styles.detailedViewSubtitle}>View your listing score analysis results</p>
                    </div>
                </div>
                <div className={styles.loadingContainer}>
                    <FaSpinner className="fa-spin" />
                    <p>Loading results...</p>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className={styles.container}>
                <div className={styles.detailedViewHeader}>
                    <button 
                        className={styles.backButton}
                        onClick={() => navigate(-1)}
                        aria-label="Back"
                    >
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M20 8.75H4.7875L11.775 1.7625L10 0L0 10L10 20L11.7625 18.2375L4.7875 11.25H20V8.75Z" fill="#737373"/>
                        </svg>
                    </button>
                    <div className={styles.detailedViewHeaderContent}>
                        <h1 className={styles.detailedViewTitle}>Result History</h1>
                        <p className={styles.detailedViewSubtitle}>View your listing score analysis results</p>
                    </div>
                </div>
                <div className={styles.errorMessage}>
                    <strong>Error:</strong> {error}
                    <button onClick={fetchListingScoreHistory} className={styles.retryButton}>
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    // Show detailed view as full page
    if (showDetailedView && selectedScoreData) {
        return (
            <div className={styles.container}>
                <div className={styles.detailedViewHeader}>
                    <button 
                        className={styles.backButton}
                        onClick={handleCloseDetailedView}
                        aria-label="Back to results"
                    >
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M20 8.75H4.7875L11.775 1.7625L10 0L0 10L10 20L11.7625 18.2375L4.7875 11.25H20V8.75Z" fill="#737373"/>
                        </svg>

                    </button>
                    <div className={styles.detailedViewHeaderContent}>
                        <h1 className={styles.detailedViewTitle}>Listing Score Analysis</h1>
                        <p className={styles.detailedViewSubtitle}>Real-time evaluation based on Amazon best practices</p>
                    </div>
                </div>

                <div className={styles.detailedViewContent}>
                    {selectedScoreData.analysis && (
                        <>
                            <ResultsSection productData={selectedScoreData} onUpgradeListing={handleUpgradeListing} />
                            
                            <section className={`${listingScoreStyles.section2} ${styles.sectionWithMargins}`}>
                                <ActionPlanSection 
                                    productData={{ analysis: selectedScoreData.analysis }} 
                                    showDetailedAnalysis={true}
                                />
                                <ComponentAnalysisSection productData={{ analysis: selectedScoreData.analysis }} />
                            </section>
                        </>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <div className={styles.detailedViewHeader}>
                {selectedBatchId ? (
                    <>
                        <button
                            className={styles.backButton}
                            onClick={handleBackToBatches}
                            aria-label="Back to batches"
                        >
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M20 8.75H4.7875L11.775 1.7625L10 0L0 10L10 20L11.7625 18.2375L4.7875 11.25H20V8.75Z" fill="#737373"/>
                            </svg>
                        </button>
                        <div className={styles.detailedViewHeaderContent}>
                            <h1 className={styles.detailedViewTitle}>Bulk Results</h1>
                            <p className={styles.detailedViewSubtitle}>{batchResults.length} ASIN{batchResults.length !== 1 ? 's' : ''} in this batch</p>
                        </div>
                        <ListingScoreDownloadButton token={authCtx.token} taskId={selectedBatchId} />
                    </>
                ) : (
                    <>
                        <button
                            className={styles.backButton}
                            onClick={() => navigate(-1)}
                            aria-label="Back"
                        >
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M20 8.75H4.7875L11.775 1.7625L10 0L0 10L10 20L11.7625 18.2375L4.7875 11.25H20V8.75Z" fill="#737373"/>
                            </svg>
                        </button>
                        <div className={styles.detailedViewHeaderContent}>
                            <h1 className={styles.detailedViewTitle}>Result History</h1>
                            <p className={styles.detailedViewSubtitle}>View your listing score analysis results</p>
                        </div>
                    </>
                )}
            </div>

            {selectedBatchId ? (
                batchResults.length > 0 ? (
                    <>
                        <ResultsGrid
                            results={batchResults}
                            onViewScore={handleViewScore}
                            currentPage={currentPage}
                            productsPerPage={PRODUCTS_PER_PAGE}
                        />
                        {batchTotalPages > 1 && (
                            <Pagination
                                currentPage={currentPage}
                                totalPages={batchTotalPages}
                                onPageChange={handleBatchPageChange}
                            />
                        )}
                    </>
                ) : (
                    <div className={styles.emptyState}>
                        <h3>No results in this batch</h3>
                        <button type="button" onClick={handleBackToBatches} className={styles.retryButton}>Back to batches</button>
                    </div>
                )
            ) : hasBatches ? (
                <div className={manageStyles.smallCardsGrid}>
                    {batches.map((batch) => (
                        <BulkScoreCard
                            key={batch.task_id}
                            batch={batch}
                            onClick={() => {
                                setSelectedBatchId(batch.task_id);
                                setCurrentPage(1);
                            }}
                        />
                    ))}
                </div>
            ) : (
                <div className={styles.emptyState}>
                    <h3>No results found</h3>
                    <p>Analyze some ASINs first to see results here.</p>
                </div>
            )}
        </div>
    );
};

export default ListingScoreResultHistory;
export { groupResultsByBatch };

